import { useCallback } from "react";

function resizeTitle(node: HTMLTextAreaElement) {
  node.style.height = "auto";
  node.style.height = `${node.scrollHeight}px`;
}

/** Native multiline title with a measured fallback; no nested scrollbar or resize handle. */
export function DocumentTitle({
  value,
  onChange,
  onSave,
}: {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  const mount = useCallback(
    (node: HTMLTextAreaElement | null) => {
      if (!node) return;
      node.value = value;
      resizeTitle(node);
      let width = node.getBoundingClientRect().width;
      let resizeFrame = 0;
      const observer = new ResizeObserver(() => {
        const nextWidth = node.getBoundingClientRect().width;
        if (nextWidth !== width) {
          width = nextWidth;
          cancelAnimationFrame(resizeFrame);
          resizeFrame = requestAnimationFrame(() => {
            resizeTitle(node);
          });
        }
      });
      observer.observe(node);
      return () => {
        observer.disconnect();
        cancelAnimationFrame(resizeFrame);
      };
    },
    [value],
  );
  return (
    <textarea
      ref={mount}
      aria-label="Page title"
      rows={1}
      maxLength={200}
      className="document-title mb-3 block w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-4xl leading-tight font-semibold tracking-tight outline-none placeholder:text-muted-foreground/60 focus-visible:shadow-[var(--focus-ring)]"
      placeholder="Untitled"
      value={value}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return;
        if (
          event.key === "Enter" ||
          (event.key === "ArrowDown" &&
            event.currentTarget.selectionStart === event.currentTarget.value.length)
        ) {
          event.preventDefault();
          event.currentTarget
            .closest("article")
            ?.querySelector<HTMLElement>('[aria-label="Document content"]')
            ?.focus();
        }
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
          event.preventDefault();
          onSave();
        }
      }}
      onChange={(event) => {
        onChange(event.target.value.replace(/\n/g, " "));
        resizeTitle(event.currentTarget);
      }}
    />
  );
}
